// File: webelieve-frontend/src/features/admin/components/settings/intake-forms/FormPreview.tsx
// EF-6.3: Live preview of the form as it will appear to public users

import React from 'react';
import type { FormSchema, FormField, FormFieldType } from './intakeFormApi';

interface FormPreviewProps {
  schema: FormSchema;
}

/** Render a single field in read-only/preview mode */
function renderField(field: FormField): React.ReactNode {
  const base =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed';

  switch (field.type as FormFieldType) {
    case 'text':
    case 'email':
    case 'phone':
    case 'number':
    case 'currency':
      return (
        <input
          type={field.type === 'currency' ? 'text' : field.type === 'phone' ? 'tel' : field.type}
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
          disabled
          className={base}
        />
      );

    case 'textarea':
      return (
        <textarea
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
          rows={3}
          disabled
          className={base + ' resize-none'}
        />
      );

    case 'date':
      return <input type="date" disabled className={base} />;

    case 'select':
      return (
        <select disabled className={base}>
          <option value="">{field.placeholder || 'Select an option...'}</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'multiselect':
      return (
        <div className="space-y-1">
          {(field.options || []).map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-500">
              <input type="checkbox" disabled className="rounded border-gray-300" />
              {opt.label}
            </label>
          ))}
          {(!field.options || field.options.length === 0) && (
            <p className="text-xs text-gray-400 italic">No options defined</p>
          )}
        </div>
      );

    case 'checkbox':
      return (
        <label className="flex items-center gap-2 text-sm text-gray-500">
          <input type="checkbox" disabled className="rounded border-gray-300" />
          {field.placeholder || field.label}
        </label>
      );

    case 'file':
      return (
        <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
          <p className="text-sm text-gray-400">Click to upload or drag and drop</p>
          <p className="text-xs text-gray-300 mt-1">PDF, DOCX, JPG up to 10MB</p>
        </div>
      );

    case 'address':
      return (
        <div className="space-y-2">
          <input type="text" placeholder="Street address" disabled className={base} />
          <div className="grid grid-cols-3 gap-2">
            <input type="text" placeholder="City" disabled className={base} />
            <input type="text" placeholder="State" disabled className={base} />
            <input type="text" placeholder="ZIP" disabled className={base} />
          </div>
        </div>
      );

    default:
      return <input type="text" disabled className={base} />;
  }
}

const FormPreview: React.FC<FormPreviewProps> = ({ schema }) => {
  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
        {/* Form header */}
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {schema.title || 'Untitled Form'}
          </h2>
          {schema.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{schema.description}</p>
          )}
        </div>

        {/* Fields */}
        <div className="p-5 space-y-5">
          {schema.fields.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic text-center py-8">
              No fields added yet. Drag fields from the palette to get started.
            </p>
          )}
          {schema.fields.map((field) => (
            <div key={field.id}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.label || 'Untitled Field'}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>

        {/* Submit button */}
        {schema.fields.length > 0 && (
          <div className="px-5 pb-5">
            <button
              disabled
              className="w-full px-4 py-2.5 bg-lex-accent text-lex-accent-contrast text-sm font-medium rounded-md opacity-75 cursor-not-allowed"
            >
              {schema.submit_button_text || 'Submit'}
            </button>
          </div>
        )}
      </div>

      <p className="text-[10px] text-gray-400 text-center mt-3">
        Preview only — form inputs are disabled
      </p>
    </div>
  );
};

export default FormPreview;
export { renderField };
